import { Card, Grid, Title } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IIssueBoardApi } from "@mcapps/shared";
import { IIssueApi, IIssueUpdateSchemaProps } from "@schemas/issueSchema";
import { IconCalendarWeek, IconNumbers } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../../openapi";
import { isDueDateOverdue } from "../../../../../../components/DisplayDueDate";
import { McTitle } from "../../../../../../components";
import FoldableSection from "../../../../../../components/FoldableSection/FoldableSection";
import McInputNew from "../../../../../../components/McInput/McInputNew";
import { IssueDisabledColumnsEnum } from "@mcapps/shared/enums";
import { ISSUE_COMPLETED_VALUE, ISSUE_EXPIRED } from "../../../issueConstants";
import { CellCtx, CellDef, createSimplePayload, guardedIssueUpdate } from "./modalUtils";

const CELL_DEFS: CellDef[] = [
  {
    id: "dates.plannedStart",
    disableKey: IssueDisabledColumnsEnum.DATESPLANNEDSTART,
    span: 4,
    render: (ctx) => (
      <McTitle title="Startdato">
        <DateInput
          styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
          clearable
          valueFormat="DD.MM.YYYY"
          onChange={(e) => {
            if (e === ctx.issue.dates?.plannedStart) {
              return;
            }
            ctx.updateSimple({
              dates: {
                plannedStart: e,
              },
            });
          }}
          value={ctx.issue.dates?.plannedStart}
          rightSection={
            ctx.issue.dates?.plannedStart == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
          }
        />
      </McTitle>
    ),
  },
  {
    id: "dates.due",
    disableKey: IssueDisabledColumnsEnum.DATESDUE,
    span: 4,
    render: (ctx) => {
      const issueIsComplete = ctx.issue.status === ISSUE_COMPLETED_VALUE || ctx.issue.status === ISSUE_EXPIRED;
      const dueDateIsOverdue = isDueDateOverdue(ctx.issue.dates?.due, issueIsComplete);

      return (
        <McTitle title="Tidsfrist">
          <DateInput
            styles={{
              input: {
                minHeight: "2.625rem",
                height: "2.625rem",
                color: dueDateIsOverdue ? "red" : undefined,
              },
            }}
            clearable
            leftSection={dueDateIsOverdue ? <i className="fa fa-warning fa-fw icon-danger" /> : undefined}
            leftSectionPointerEvents="none"
            valueFormat="DD.MM.YYYY"
            onChange={(e) => {
              if (e === ctx.issue.dates?.due) {
                return;
              }
              ctx.updateSimple({
                dates: {
                  due: e,
                },
              });
            }}
            value={ctx.issue.dates?.due}
            rightSection={
              dueDateIsOverdue === false &&
              ctx.issue.dates?.due == null && <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
            }
          />
        </McTitle>
      );
    },
  },
  // Empty span to fill the gap when only one of plannedStart/due is visible
  {
    id: "___empty___",
    disableKey: "___empty___" as IssueDisabledColumnsEnum,
    span: (ctx) => {
      const ps = ctx.issueBoard?.disabledColumns?.includes(IssueDisabledColumnsEnum.DATESPLANNEDSTART) === false;
      const due = ctx.issueBoard?.disabledColumns?.includes(IssueDisabledColumnsEnum.DATESDUE) === false;

      if (ps && due) {
        return 4;
      }

      if ((ps && !due) || (!ps && due)) {
        return 8;
      }

      return 0;
    },
    render: () => <></>,
    when: (ctx) => {
      const psDisabled = ctx.issueBoard?.disabledColumns?.includes(IssueDisabledColumnsEnum.DATESPLANNEDSTART);
      const dueDisabled = ctx.issueBoard?.disabledColumns?.includes(IssueDisabledColumnsEnum.DATESDUE);

      return !psDisabled || !dueDisabled;
    },
  },
  {
    id: "hours.originalEstimate",
    disableKey: IssueDisabledColumnsEnum.HOURSORIGINALESTIMATE,
    span: 4,
    render: (ctx) => (
      <McTitle title="Estimerte timer">
        <McInputNew
          rightSection={<IconNumbers color="var(--mantine-color-warmgray-3)" />}
          type={"number"}
          onBlur={(e) => {
            const newValue = e.target.value === "" ? null : Number(e.target.value);

            if (newValue === ctx.issue.hours?.orignalEstimate) {
              return;
            }

            ctx.updateSimple({
              hours: {
                orignalEstimate: newValue,
              },
            });
          }}
          value={ctx.issue.hours?.orignalEstimate}
        />
      </McTitle>
    ),
  },
  {
    id: "hours.completed",
    disableKey: IssueDisabledColumnsEnum.HOURSCOMPLETED,
    span: 4,
    render: (ctx) => (
      <McTitle title="Brukte timer">
        <McInputNew
          rightSection={<IconNumbers color="var(--mantine-color-warmgray-3)" />}
          type={"number"}
          onBlur={(e) => {
            const newValue = e.target.value === "" ? null : Number(e.target.value);

            if (newValue === ctx.issue.hours?.completed) {
              return;
            }

            ctx.updateSimple({
              hours: {
                completed: newValue,
              },
            });
          }}
          value={ctx.issue.hours?.completed}
        />
      </McTitle>
    ),
  },
  {
    id: "hours.remaining",
    disableKey: IssueDisabledColumnsEnum.HOURSREMAINING,
    span: 4,
    render: (ctx) => (
      <McTitle title="Gjenstående timer">
        <McInputNew
          rightSection={<IconNumbers color="var(--mantine-color-warmgray-3)" />}
          type={"number"}
          onBlur={(e) => {
            const newValue = e.target.value === "" ? null : Number(e.target.value);

            if (newValue === ctx.issue.hours?.remaining) {
              return;
            }

            ctx.updateSimple({
              hours: {
                remaining: newValue,
              },
            });
          }}
          value={ctx.issue.hours?.remaining}
        />
      </McTitle>
    ),
  },
];

const IssueItemTimeSection = ({
  issue,
  projectId,
  issueBoard,
}: {
  issue: IIssueApi;
  projectId: string;
  issueBoard: IIssueBoardApi;
}) => {
  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);
  const updateSimple = useCallback(
    async (prop: IIssueUpdateSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const disabledSet = useMemo(() => new Set(issueBoard?.disabledColumns ?? []), [issueBoard?.disabledColumns]);

  const ctx: CellCtx = useMemo(
    () => ({
      issue,
      updateSimple,
      issueBoard: issueBoard,
      projectId: null,
      usersComboboxData: null,
      multiUsersComboboxData: null,
      bucketOptions: null,
      bucketReadOnly: true,
      typeOptions: null,
      typeAheadPNSItems: null,
      issueTags: null,
      tagsLoading: null,
      updateUser: null,
      updateDescription: async () => undefined,
    }),
    [issue, projectId, updateSimple],
  );

  const visibleCells = useMemo(() => {
    return CELL_DEFS.filter((cell) => {
      // if the cell is disableable, check it
      if (cell.disableKey != null && disabledSet.has(cell.disableKey)) return false;
      return true;
    }).filter((cell) => (cell.when ? cell.when(ctx) : true));
  }, [disabledSet, ctx]);

  if (visibleCells.length === 0) {
    return null;
  }

  return (
    <FoldableSection storageKey="issue-modal-time">
      <FoldableSection.Header>
        <Title order={6}>Tidsplan</Title>
      </FoldableSection.Header>
      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Grid flex={1}>
            {visibleCells.map((cell) => (
              <Grid.Col key={cell.id} span={typeof cell.span === "number" ? cell.span : cell.span(ctx)}>
                {cell.render(ctx)}
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};

export default IssueItemTimeSection;
